<template>
    <div class="min-h-screen bg-background font-sans selection:bg-primary/10">


        <main class="pt-32 pb-20 container mx-auto px-4">
            <transition name="fade-up" appear>
                <div class="text-center mb-16">
                    <h1 class="text-4xl md:text-6xl font-serif text-primary mb-6">The Collection</h1>
                    <p class="max-w-2xl mx-auto text-muted-foreground">
                        A curated selection of paintings exploring emotion, light, and the human spirit.
                    </p>
                </div>
            </transition>

            <!-- Filter -->
            <div class="flex flex-wrap justify-center gap-4 mb-12">
                <button v-for="cat in categories" :key="cat" @click="filter = cat" :class="[
                    'text-sm uppercase tracking-widest px-4 py-2 transition-all duration-300 border-b border-transparent',
                    filter === cat
                        ? 'text-primary border-primary'
                        : 'text-muted-foreground hover:text-primary'
                ]">
                    {{ cat }}
                </button>
            </div>

            <!-- Masonry-style Grid -->
            <transition-group tag="div" name="list" class="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
                <div v-for="art in filteredArtworks" :key="art.id" class="break-inside-avoid"
                    @click="openArtworkModal(art)">
                    <div class="group cursor-zoom-in relative">
                        <div class="relative overflow-hidden bg-secondary/20">
                            <img :src="art.images?.length > 0 ? art.images[0] : ''" :alt="art.title" :class="[
                                'w-full h-auto transition-transform duration-700 group-hover:scale-105',
                                art.status === 'sold' && 'grayscale-[0.5] opacity-90'
                            ]" loading="lazy" />
                            <div v-if="art.status === 'sold'" class="absolute top-4 right-4 z-10">
                                <span
                                    class="bg-red-600 text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm">Sold</span>
                            </div>
                            <div
                                class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                        </div>
                        <div class="mt-4 flex justify-between items-baseline">
                            <div>
                                <h3
                                    class="font-serif text-lg text-primary group-hover:text-primary/80 transition-colors">
                                    {{ art.title }}</h3>
                                <p class="text-xs text-muted-foreground mt-1">{{ art.size }}</p>
                            </div>
                            <div class="text-right">
                                <span class="block text-sm font-medium">${{ art.price ?? 'N/A' }}</span>
                                <span class="text-[10px] text-muted-foreground uppercase tracking-wider">{{ art.category
                                }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>
        </main>

        <!-- Artwork Modal -->
        <transition name="fade">
            <div v-if="selectedArtwork" @click.self="closeArtworkModal"
                class="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                <transition name="zoom">
                    <div v-if="selectedArtwork"
                        class="max-w-5xl w-full h-[90vh] md:h-auto bg-transparent shadow-none flex flex-col md:flex-row gap-0 overflow-hidden">
                        <!-- Image Side -->
                        <button @click="closeArtworkModal" aria-label="Close"
                            class="absolute top-4 right-4 z-[999] w-10 h-10 flex items-center justify-center
                            rounded-full bg-black/60 text-white  hover:bg-black/80 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white">
                            X
                        </button>
                        <div
                            :class="['relative flex-1 bg-black/90 flex items-center justify-center transition-all duration-500', showPurchaseForm ? 'hidden md:flex md:w-1/2' : 'w-full h-full']">
                            <div class="relative w-full h-full flex items-center justify-center p-4 md:p-12">

                                <!-- LEFT -->
                                <button v-if="selectedArtwork.images.length > 1" @click.stop="prevImage"
                                    class="absolute left-4 md:left-8 bg-white/50 text-black p-3 rounded-full z-10 ">
                                    ‹
                                </button>

                                <!-- IMAGE -->
                                <img :src="selectedArtwork.images[currentImageIndex]" :alt="selectedArtwork.title"
                                    class="max-h-[80vh] max-w-full object-contain shadow-2xl transition-all duration-300" />

                                <!-- RIGHT -->
                                <button v-if="selectedArtwork.images.length > 1" @click.stop="nextImage"
                                    class="absolute right-4 md:right-8 bg-white/50 text-black p-3 rounded-full z-10 ">
                                    ›
                                </button>

                                <!-- DOTS -->
                                <div v-if="selectedArtwork.images.length > 1" class="absolute bottom-4 flex gap-2">
                                    <span v-for="(img, i) in selectedArtwork.images" :key="i"
                                        @click.stop="currentImageIndex = i" class="w-2 h-2 rounded-full cursor-pointer"
                                        :class="currentImageIndex === i ? 'bg-white' : 'bg-white/40'">
                                    </span>
                                </div>

                            </div>
                            <!-- <button @click="closeArtworkModal"
                                class="absolute top-4 right-4 z-50 bg-black/50 p-2 rounded-full text-white">
                                
                               
                            </button> -->
                        </div>

                        <!-- Details / Form Side -->
                        <transition name="slide-fade" mode="out-in">
                            <div v-if="showPurchaseForm"
                                class="fixed inset-0 z-50 bg-background md:static md:z-auto w-full md:w-[450px] h-full md:h-auto flex flex-col">
                                <!-- Purchase Form -->
                                <div
                                    class="p-6 bg-card text-card-foreground rounded-lg flex-1 overflow-y-auto md:max-h-[85vh]">
                                    <div class="flex justify-between items-center mb-6">
                                        <h3 class="text-xl font-serif">Purchase Request</h3>
                                        <button @click="showPurchaseForm = false" class="p-2">
                                            <!-- <XIcon class="h-4 w-4" /> -->
                                        </button>
                                    </div>
                                    <div class="flex gap-4 mb-6 bg-muted/30 p-4 rounded-md">
                                        <div class="w-16 h-16 rounded overflow-hidden flex-shrink-0">
                                            <img :src="selectedArtwork?.images[0]" :alt="selectedArtwork.title"
                                                class="w-full h-full object-cover" />
                                        </div>
                                        <div>
                                            <h4 class="font-serif font-medium">{{ selectedArtwork.title }}</h4>
                                            <p class="text-sm text-muted-foreground">{{ selectedArtwork.size }}</p>
                                            <p class="text-sm font-semibold mt-1">${{ selectedArtwork.price }}</p>
                                        </div>
                                    </div>
                                    <form class="space-y-4">
                                        <div class="grid gap-2">
                                            <label for="name">Full Name</label>
                                            <input v-model="formData.name" id="name" required
                                                class="border p-2  w-full border-black" />
                                        </div>
                                        <div class="grid gap-2">
                                            <label for="email">Email Address</label>
                                            <input v-model="formData.email" id="email" type="email" required
                                                class="border p-2  w-full border-black" />
                                        </div>
                                        <div class="grid gap-2">
                                            <label for="phone">Phone Number</label>
                                            <input v-model="formData.phone_no" id="phone" type="tel" required
                                                class="border p-2  w-full border-black" />
                                        </div>
                                        <div class="grid gap-2">
                                            <label for="address">Shipping Address</label>
                                            <textarea v-model="formData.address" id="address" required
                                                class="min-h-[100px] border p-2  w-full border-black"></textarea>
                                        </div>
                                        <button type="submit" :disabled="isSubmitting" @click="handleSubmit"
                                            class="w-full mt-4 bg-primary text-white py-3  disabled:opacity-50">
                                            {{ isSubmitting ? "Processing..." : "Proceed" }}
                                        </button>
                                        <button type="submit" @click="sendWhatsapp" :disabled="isSubmitting"
                                            class="w-full mt-4 bg-green-500 text-white py-3  disabled:opacity-50">
                                            Send WhatsApp
                                        </button>
                                        <p class="text-xs text-muted-foreground text-center mt-2">
                                            *Payment gateway integration coming soon. This will send a reservation
                                            request.
                                        </p>
                                    </form>
                                </div>
                            </div>
                            <div v-else
                                class="hidden md:flex flex-col justify-center p-8 bg-white/95 backdrop-blur-sm md:w-[350px] lg:w-[400px]">
                                <!-- Desktop Details Panel -->
                                <div class="space-y-6">
                                    <div>
                                        <h2 class="text-3xl font-serif text-primary mb-2">{{ selectedArtwork.title }}
                                        </h2>
                                        <p class="text-muted-foreground italic">{{ selectedArtwork.category }}</p>
                                    </div>
                                    <div class="space-y-2 py-6 border-y border-border/40">
                                        <div class="flex justify-between">
                                            <span class="text-muted-foreground">Dimensions</span>
                                            <span class="font-medium">{{ selectedArtwork.size }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-muted-foreground">Medium</span>
                                            <span class="font-medium">{{ art_medium }}</span>
                                        </div>
                                        <div class="flex justify-between items-center pt-2">
                                            <span class="text-muted-foreground">Price</span>
                                            <span class="text-2xl font-serif text-primary">${{ selectedArtwork.price
                                            }}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <!-- <button v-if="selectedArtwork.status === 'available'" -->
                                        <button v-if="selectedArtwork.outofstock === 0" @click="showPurchaseForm = true"
                                            class="w-full rounded-none tracking-widest uppercase bg-primary text-white py-3">
                                            Purchase Artwork
                                        </button>
                                        <button v-if="selectedArtwork.outofstock === 1" @click="showPurchaseForm = true"
                                            class="w-full rounded-none tracking-widest uppercase bg-primary text-white py-3">
                                            Re-order
                                        </button>
                                        <p class="text-xs text-center text-muted-foreground mt-4 leading-relaxed">
                                            {{ selectedArtwork.outofstock === 0 ?
                                                "Worldwide shipping available. Authenticity certificate included."
                                                : "This piece has found a home. Contact us for commissions." }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </transition>
            </div>
        </transition>

        <!-- Simple Toast Notification -->
        <transition name="fade">
            <div v-if="toast.visible" class="fixed top-5 right-5 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50">
                <p class="font-bold">{{ toast.title }}</p>
                <p>{{ toast.description }}</p>
            </div>
        </transition>
    </div>
</template>

<script>
// import { XIcon } from 'lucide-vue-next';
import api from '@/services/api';
import { capitalize } from 'vue';

export default {
    name: 'GalleryPage',
    data() {
        return {
            artworks: [],
            categories: ['All'],
            filter: 'All',
            selectedArtwork: null,
            showPurchaseForm: false,
            formData: {
                name: null,
                email: null,
                phone_no: null,
                address: null,
            },
            isSubmitting: false,
            toast: {
                visible: false,
                title: '',
                description: ''
            },
            art_medium: 'Watercolor on Paper',
            currentImageIndex: 0,
        };
    },
    computed: {
        filteredArtworks() {

            if (this.filter === 'All') {
                return this.artworks;
            }

            return this.artworks.filter(art =>
                art.categories?.some(cat => cat.name === this.filter)
            );
        }
    },
    mounted() {
        this.fetchProducts();
    },
    methods: {
        async fetchProducts() {
            try {
                let param = {
                    fetchColumns: 'id,name,cp,sp,mrp,currency,width,height,measurement_unit,outofstock'
                }
                const response = await api.post('api/product/getwithcategories', param);

                const products = response.data?.data || [];

                const categorySet = new Set();

                this.artworks = products.map(product => {
                    // collect categories
                    product.product_category?.forEach(cat => {
                        if (cat.name !== 'Gallery' && cat.name !== 'Commission') {
                            categorySet.add(cat.name);
                        }
                    });

                    let width = Number(product.width);
                    let height = Number(product.height);
                    let size = `${width} x ${height} ${capitalize(product.measurement_unit)}`;
                    let status = product.outofstock === 0 ? 'available' : 'sold';

                    return {
                        id: product.id,
                        title: product.name,
                        price: product.sp,
                        size,
                        images: product.productmedia?.map(m => m.small_fullurl) || [],
                        amazonid: product.productmedia?.[0]?.small_amazonsignedid,
                        status,
                        outofstock: product.outofstock,
                        // if product has multiple categories, use first for display
                        category: product.product_category?.[1]?.name ?? 'Uncategorized',
                        categories: product.product_category
                    };
                });

                // push unique categories
                this.categories = ['All', ...Array.from(categorySet)];

            } catch (error) {
                console.error('Failed to fetch products:', error);
            }
        },

        openArtworkModal(artwork) {
            console.log('Opening artwork modal for:', artwork);
            this.selectedArtwork = artwork;
            this.currentImageIndex = 0;
            document.body.style.overflow = 'hidden';
        },

        closeArtworkModal() {
            this.showPurchaseForm = false;
            this.selectedArtwork = null;
            document.body.style.overflow = '';
        },

        // handlePurchaseSubmit() {
        //     this.isSubmitting = true;
        //     setTimeout(() => {
        //         this.isSubmitting = false;
        //         this.showToast(
        //             "Order Request Received",
        //             `Thank you ${this.formData.name}! We will contact you shortly about "${this.selectedArtwork.title}".`
        //         );
        //         this.showPurchaseForm = false;
        //         this.resetForm();
        //     }, 1500);
        // },
        async handleSubmit() {
            this.isSubmitting = true;
            try {
                const payload = {
                    ...this.formData,
                    size_json: JSON.stringify({ label: this.selectedArtwork.size }),
                    art_medium_json: JSON.stringify({ label: this.art_medium }),
                    amazonid: this.selectedArtwork.amazonid,
                    image_url: this.selectedArtwork.src,
                    price: this.selectedArtwork.price,
                    productid: this.selectedArtwork.id,
                    outofstock: this.selectedArtwork.outofstock,
                };

                const response = await api.post('api/artpulse/commission/save', payload);

                if (response.data?.success) {
                    this.showToast(
                        "Order Request Received",
                        `Thank you ${this.formData.name}! We will contact you shortly about "${this.selectedArtwork.title}".`
                    );
                    this.showPurchaseForm = false;
                } else {
                    throw new Error(response.data?.message || 'API returned failure');
                }

            } catch (error) {
                console.error('purchase API Error:', error);

                const message =
                    error.response?.data?.message ||
                    error.message ||
                    "Something went wrong. Please try again later.";

                this.showToast("Error", message);

            } finally {
                this.showPurchaseForm = false;
                this.isSubmitting = false;
                this.resetForm();
            }
        },
        sendWhatsapp() {


            if (this.formData.email == '' || this.formData.email == null) {
                this.showToast("Error", "Please enter your email and phone number.");
                return;
            }
            if (this.formData.phone_no == '' || this.formData.phone_no == null) {
                this.showToast("Error", "Please enter your email and phone number.");
                return;
            }
            this.isSubmitting = true;
            try {
                const data = {
                    heading: this.selectedArtwork.outofstock === 0 ? "Artwork Purchase Request" : "Artwork Re-order Request",
                    productid: this.selectedArtwork.id,
                    outofstock: this.selectedArtwork.outofstock,
                    name: this.formData.name,
                    email: this.formData.email,
                    phone_no: this.formData.phone_no,
                    address: this.formData.address,
                    size_json: JSON.stringify({ label: this.selectedArtwork.size }),
                    art_medium_json: JSON.stringify({ label: this.art_medium }),
                    price: this.selectedArtwork.price,
                    productTitle: this.selectedArtwork.title,
                }

                const size = JSON.parse(data.size_json).label
                const medium = JSON.parse(data.art_medium_json).label

                const message = `
                
*${data.heading}*

*Customer Details*
• Name: ${data.name}
• Email: ${data.email}
• Phone: ${data.phone_no}

*Shipping Address*
${data.address}

*Artwork Details*
• Title: ${data.productTitle}
• Price: ₹${data.price}
• Size: ${size}
• Medium: ${medium}
                `

                const encodedMessage = encodeURIComponent(message.trim())

                window.open(
                    `https://wa.me/14806360819?text=${encodedMessage}`,
                    '_blank'
                )
            } catch (error) {
                console.error(error)
            } finally {
                this.showPurchaseForm = false;
                this.isSubmitting = false;
                this.resetForm();
            }
        },

        resetForm() {
            this.formData = { name: '', email: '', phone_no: '', address: '' };
            this.selectedArtwork = null;
        },

        showToast(title, description) {
            this.toast = { visible: true, title, description };
            setTimeout(() => (this.toast.visible = false), 5000);
        },
        nextImage() {
            const total = this.selectedArtwork.images.length;
            this.currentImageIndex = (this.currentImageIndex + 1) % total;
        },
        prevImage() {
            const total = this.selectedArtwork.images.length;
            this.currentImageIndex =
                (this.currentImageIndex - 1 + total) % total;
        },
    }
};
</script>

<style scoped>
.fade-up-enter-active,
.fade-up-leave-active {
    transition: opacity 0.8s, transform 0.8s;
}

.fade-up-enter-from,
.fade-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

.list-enter-active,
.list-leave-active {
    transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.zoom-enter-active,
.zoom-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.zoom-enter-from,
.zoom-leave-to {
    opacity: 0;
    transform: scale(0.95);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateX(50px);
    opacity: 0;
}
</style>